import { Injectable, inject } from '@angular/core';
import { IChatAdapter } from '../interfaces';
import { FoundryAgentAdapter } from './foundry-agent.adapter';
import { N8nChatAdapter } from './n8n-chat.adapter';

/**
 * Chat backend types
 */
export type ChatBackendType = 'foundry' | 'n8n';

/**
 * Configuration for chat backend
 */
export interface IChatBackendConfig {
	backend: ChatBackendType;
	n8nWebhookUrl?: string;
	n8nChatInputKey?: string;
	n8nSessionKey?: string;
	n8nIncludeAuthToken?: boolean;
	n8nAdditionalHeaders?: Record<string, string>;
}

/**
 * Factory service that provides the appropriate chat adapter
 * based on configuration. Allows easy switching between backends.
 */
@Injectable({
	providedIn: 'root'
})
export class ChatAdapterFactory {
	private _foundryAdapter = inject(FoundryAgentAdapter);
	private _n8nAdapter = inject(N8nChatAdapter);
	private _currentBackend: ChatBackendType = 'foundry'; // Default to foundry

	/**
	 * Get the current chat adapter based on configuration
	 */
	getAdapter(): IChatAdapter {
		switch (this._currentBackend) {
			case 'n8n':
				return this._n8nAdapter;
			case 'foundry':
			default:
				return this._foundryAdapter;
		}
	}

	/**
	 * Configure and switch to a different backend
	 */
	configure(config: IChatBackendConfig): void {
		this._currentBackend = config.backend;

		// If switching to n8n, configure the webhook
		if (config.backend === 'n8n' && config.n8nWebhookUrl) {
			this._n8nAdapter.configure(
				config.n8nWebhookUrl,
				config.n8nChatInputKey,
				config.n8nSessionKey,
				config.n8nIncludeAuthToken ?? true,
				config.n8nAdditionalHeaders
			);
		}
	}

	/**
	 * Get the current backend type
	 */
	getCurrentBackend(): ChatBackendType {
		return this._currentBackend;
	}
}
